import React, { useCallback, useEffect, useMemo, useRef } from 'react';

// DEPENDENCIES
import clsx from 'clsx';
import isEqual from 'lodash/isEqual';
import isNumber from 'lodash/isNumber';
import isUndefined from 'lodash/isUndefined';
import toString from 'lodash/toString';
import debounce from 'lodash/debounce';
import { useFormikContext } from 'formik';
import { useSelector } from 'react-redux';
import HTMLReactParser from 'html-react-parser';

// GLOBAL VARIABLES
import { ENDPOINTS } from 'endpoints';
import { RETAIL_WHOLESALE, WHOLESALE_FORMAT } from 'codes';

// FUNCTIONS/HELPERS
import { apiFetch } from 'functions';
import usePrevious from 'helpers/usePrevious';
import { useUploadHelpers } from './helpers/useUploadHelpers';

// GLOBAL CONTEXT
import useRecordView from 'helpers/getRecordView';

// CORE COMPONENTS
import Form from 'core/form/Form';

// PARTS
import Card from 'parts/card/Card';
import SpotInstructionAttachments from './parts/SpotInstructionAttachments';

// STYLES
import styles from './spotInstructions.module.scss';

// VARIABLES
const LOOKUP_ARGS = {
  endpoint: ENDPOINTS.admin.spotInstructions.common.list,
  params: {
    isActive: 1
  }
};
const OPTION_KEYS = {
  label: 'common_spot_instruction_title',
  value: 'common_spot_instruction_id'
};
const ITEM_CONSTRUCTOR = obj => ({
  spot_instruction_id: '',
  spot_instruction: obj?.common_spot_instruction_title,
  is_generated: !!obj,
  is_completed: '',
  is_task: false,
  is_task_completed: false,
  upload_document_required: obj?.upload_document_required,
  arySpotInstructionAttachment: [],
});

// TODO: Better way to handle thise?
const trueOrFalse = v => (v === 'true' ? true : v === 'false' ? false : v);

// FORM
const SpotInstructionsForm = () => {
  const { setFieldValue, values } = useFormikContext() || {};
  const { isAdd } = useRecordView();
  const { stores = [] } = useSelector(state => state.user || {});
  const dealership = useSelector(state => state.dealership);

  const dealerState = useMemo(
    () => stores.find(store => store.make_dealer_id === dealership)?.dealer_state,
    [stores, dealership]
  );

  const {
    sale_type_id,
    sale_type_category_id,
    customer_is_employee,
    employee_employer_name,
    employee_work_id,
    tag_state_code,
    registration_state,
    registration_city,
    third_party_tag_title,
    wholesale_format_id,
    aryRebate
  } = useMemo(() => values || {}, [values]);

  const isWholesaleSimplified = useMemo(
    () =>
      +sale_type_category_id === RETAIL_WHOLESALE.wholesale.numeric &&
      +wholesale_format_id === WHOLESALE_FORMAT.simplified.numeric,
    [sale_type_category_id, wholesale_format_id]
  );

  // Normalize to flat array of empty string, string or boolean values
  const automatedDependencies = useMemo(() => {
    const rebate = (aryRebate || []).reduce((acc, curr) => {
      acc.push(curr?.item_Amount);
      acc.push(curr?.item_name);
      return acc;
    }, []);
    return [
      sale_type_category_id,
      sale_type_id,
      customer_is_employee,
      employee_employer_name,
      employee_work_id,
      dealerState,
      tag_state_code,
      registration_state,
      registration_city,
      third_party_tag_title,
      ...(!isWholesaleSimplified ? rebate : [])
    ].map(d => ([undefined, null, ''].includes(d) ? '' : isNumber(d) ? toString(d) : d));
  }, [
    aryRebate,
    sale_type_category_id,
    sale_type_id,
    customer_is_employee,
    employee_employer_name,
    employee_work_id,
    dealerState,
    tag_state_code,
    registration_state,
    registration_city,
    third_party_tag_title,
    isWholesaleSimplified
  ]);

  const prevAutomatedDependencies = usePrevious(automatedDependencies);

  const handleChange = useCallback(
    (item, i) => {
      if (!isAdd && item.spot_instruction_id !== '') {
        setFieldValue(`arySpotInstruction[${i}].isUpdated`, true);
      }
    },
    [setFieldValue, isAdd]
  );

  // TODO: Why need to return true/false?
  const handleDelete = useCallback(
    (item, i) => {
      if (!isAdd && item.spot_instruction_id !== '') {
        setFieldValue(`arySpotInstruction[${i}].isUpdated`, false);
        setFieldValue(`arySpotInstruction[${i}].isDeleted`, true);
        return true;
      } else return false;
    },
    [setFieldValue, isAdd]
  );

  const fetchAutomated = useCallback(
    params => {
      const {
        daily_sales_inventory_id,
        sale_type_category_id,
        sale_type_id,
        customer_is_employee,
        employee_employer_name,
        employee_work_id,
        tag_state_code,
        registration_state,
        registration_city,
        aryRebate,
        third_party_tag_title,
        arySpotInstruction = []
      } = params;
      apiFetch({
        method: 'POST',
        endpoint: ENDPOINTS.sales.automatedSpotInstructions,
        params: {
          daily_sales_inventory_id,
          sale_type_category_id,
          sale_type_id,
          customer_is_employee: trueOrFalse(customer_is_employee),
          employee_employer_name,
          employee_work_id,
          dealer_state: dealerState,
          tag_state_code,
          registration_state,
          registration_city,
          third_party_tag_title: trueOrFalse(third_party_tag_title),
          ...(!isWholesaleSimplified ? { aryRebate } : {})
        },
        onSuccess: d => {
          let automated = [];
          if (d?.result?.length) {
            automated = d.result.map(r => ({
              automated_spot_instruction_id: r.automated_spot_instruction_id,
              spot_instruction_id: '',
              spot_instruction: r.automated_spot_instruction_title,
              is_generated: true,
              is_completed: false,
              is_task: false,
              is_task_completed: false,
              upload_document_required: r.upload_document_required
            }));
          }

          const existing = [];
          let newAutomated = [...automated];

          arySpotInstruction.forEach(s => {
            const matchingIndex = newAutomated.findIndex(a => a.spot_instruction === s.spot_instruction);

            // Keep any existing automated
            if (matchingIndex > -1) {
              existing.push(s);
              newAutomated.splice(matchingIndex, 1);
            }

            // Keep any manually added or already saved
            else if (isUndefined(s.automated_spot_instruction_id)) {
              existing.push(s);
            }
          });

          // Order is existing with any new automated spot instructions at the end
          setFieldValue('arySpotInstruction', [...existing, ...newAutomated]);
        }
      });
    },
    [setFieldValue, dealerState, isWholesaleSimplified]
  );

  // debounce to avoid fetching on keystroke
  const debouncedFetchAutomated = useRef(debounce(fetchAutomated, 1000)).current;

  // fetch automated when any of the automatedDependencies change
  useEffect(() => {
    if (!isEqual(prevAutomatedDependencies, automatedDependencies)) {
      debouncedFetchAutomated(values);
    }
  }, [prevAutomatedDependencies, automatedDependencies, debouncedFetchAutomated, values]);

  useEffect(() => {
    return () => {
      debouncedFetchAutomated.cancel();
    };
  }, [debouncedFetchAutomated]);

  return (
    <Form.Body>
      <Form.Table
        name="arySpotInstruction"
        itemLabel="Spot Instruction"
        lookup="commonSpotInstructions"
        lookupArgs={LOOKUP_ARGS}
        optionKeys={OPTION_KEYS}
        sortOptions
        itemConstructor={ITEM_CONSTRUCTOR}
        buttons={[
          {
            as: SpotInstructionAttachments.Button,
            icon: 'attachment',
            label: 'Add Attachment'
          }
        ]}
        useCustom
        buttonBefore
        itemWrapper={useUploadHelpers}
        handleDelete={(item, i) => handleDelete(item, i)}
        className={styles.table}
      >
        {(item = {}, i) => {
          return [
            // Always render Form.PlainText and Form.Control to prevent formik from removing value
            // Need Form.PlainText to render possible HTML
            <Form.PlainText
              cols={{ className: clsx(item.is_generated ? 'd-flex' : 'd-none', 'align-items-center') }}
              value={HTMLReactParser(item.spot_instruction || '')}
            />,
            <Form.Control
              name="spot_instruction"
              label="Description"
              maxLength={100}
              onChange={() => handleChange(item, i)}
              colWidth={18}
              required
              formGroup={{
                className: clsx(item.is_generated && 'd-none')
              }}
            />,
            <Form.YesNo
              name="is_task"
              text="Add as Task"
              type="checkbox"
              onChange={() => handleChange(item, i)}
              numeric={false}
              colWidth={6}
            />,
            <SpotInstructionAttachments attachments={item?.arySpotInstructionAttachment} index={i} />
          ];
        }}
      </Form.Table>
    </Form.Body>
  );
};

// EXPORT
export default function SpotInstructions(props) {
  return <Card title="Spot Instructions" body={<SpotInstructionsForm {...props} />} />;
}
